import { invokeEdgeFunction } from '../observability/invokeEdgeFunction';

export type ProvisionIdentityRole = 'driver' | 'client' | 'fleet_owner' | 'admin';

export interface ProvisionAuthResult {
  userId: string;
  email: string;
  role: ProvisionIdentityRole;
  /** One-time setup / recovery link — share securely; never email a long-lived password */
  setupLink?: string | null;
  /**
   * @deprecated Always empty after AU recovery hardening. Kept for type compatibility.
   */
  password?: string;
}

export async function provisionAuthUser(params: {
  role: ProvisionIdentityRole;
  email: string;
  phone?: string;
  driverId?: string;
  fleetOperatorId?: string;
  password?: string;
  redirectTo?: string;
  portalRoleId?: string;
  portalRoleCode?: string;
  displayName?: string;
}): Promise<ProvisionAuthResult> {
  const { data, error } = await invokeEdgeFunction<ProvisionAuthResult & { error?: string }>(
    'provision-user',
    {
      body: {
        role: params.role,
        email: params.email,
        phone: params.phone,
        driver_id: params.driverId,
        fleet_operator_id: params.fleetOperatorId,
        password: params.password,
        redirect_to: params.redirectTo,
        portal_role_id: params.portalRoleId,
        portal_role_code: params.portalRoleCode,
        display_name: params.displayName,
      },
    }
  );

  if (error) {
    throw error;
  }

  if (!data || typeof data !== 'object' || 'error' in data) {
    const message = data && typeof data === 'object' && 'error' in data
      ? String((data as { error: unknown }).error)
      : 'Failed to provision auth user';
    throw new Error(message);
  }

  const result = data as ProvisionAuthResult;
  // Never surface a long-lived password to the UI
  return { ...result, password: '' };
}

// ---------------------------------------------------------------------------
// Pure authorization decision logic.
// Mirrored by supabase/functions/provision-user/index.ts. Keep in sync.
// Unit-tested in provisionUser.test.ts.
// ---------------------------------------------------------------------------

export type CallerRole = 'admin' | 'fleet_owner';

export interface ProvisionRequestBody {
  action?: 'provision' | 'set_status';
  role?: ProvisionIdentityRole;
  email?: string;
  fleet_operator_id?: string;
  portal_role_id?: string;
  portal_role_code?: string;
  user_id?: string;
  status?: 'active' | 'disabled';
}

export interface CallerContext {
  callerRole: CallerRole;
  /** Permission codes the caller holds (e.g. system.users.manage, team.manage). */
  permissions: Set<string>;
  /** Caller's own fleet_operator_id (resolved from portal_user_profiles). Null for admin. */
  callerFleetId: string | null;
  /** True if caller is the primary manager of callerFleetId. */
  isPrimaryManager: boolean;
}

export interface TargetContext {
  /** Target user's existing portal profile fleet_operator_id (null = platform user / no profile). */
  targetFleetId: string | null;
  /** True if target is the primary manager of targetFleetId. */
  targetIsPrimary: boolean;
  /** Identity role on the existing auth user (if any). */
  targetIdentityRole?: string | null;
}

export interface FleetContext {
  /** True when fleet_operators.primary_manager_uid is already set for the target fleet. */
  hasPrimaryManager: boolean;
}

export interface ProvisionDecision {
  ok: boolean;
  status: number;
  error?: string;
  /** Fleet operator id to bind to the new/updated profile. */
  resolvedFleetOperatorId?: string;
  /** Portal role code to assign. */
  resolvedPortalRoleCode?: string;
}

export const FLEET_PORTAL_ROLE_CODES = ['fleet_manager', 'fleet_dispatcher', 'fleet_read_only'] as const;
export type FleetPortalRoleCode = (typeof FLEET_PORTAL_ROLE_CODES)[number];

/**
 * Decide whether the caller may perform the requested provision/set_status
 * action, and resolve the fleet_operator_id + portal_role_code to apply.
 *
 * Spec: docs/superpowers/specs/2026-07-26-fleet-user-management-design.md §2/§4.
 */
export function resolveProvisionAuthorization(
  body: ProvisionRequestBody,
  caller: CallerContext,
  target?: TargetContext,
  fleet?: FleetContext,
): ProvisionDecision {
  const action = body.action ?? 'provision';

  // ---- set_status branch ----
  if (action === 'set_status') {
    if (!body.user_id || !body.status) {
      return { ok: false, status: 400, error: 'user_id and status required' };
    }
    if (caller.callerRole === 'admin') {
      const tgt = target ?? { targetFleetId: null, targetIsPrimary: false };
      const isFleetMember = tgt.targetFleetId !== null;
      const allowed =
        (isFleetMember && caller.permissions.has('fleet_operators.members.disable')) ||
        (!isFleetMember && caller.permissions.has('system.users.manage')) ||
        (caller.permissions.has('system.users.manage') && caller.permissions.has('fleet_operators.members.disable'));
      if (!allowed) {
        return { ok: false, status: 403, error: 'Forbidden' };
      }
      if (body.status === 'disabled' && tgt.targetIsPrimary) {
        return { ok: false, status: 400, error: 'cannot disable the primary manager; transfer primary first' };
      }
      return { ok: true, status: 200 };
    }
    // fleet_owner
    if (!caller.permissions.has('team.manage')) {
      return { ok: false, status: 403, error: 'Forbidden' };
    }
    if (!caller.callerFleetId) {
      return { ok: false, status: 403, error: 'Forbidden' };
    }
    const tgt = target ?? { targetFleetId: null, targetIsPrimary: false };
    if (tgt.targetFleetId !== caller.callerFleetId) {
      return { ok: false, status: 403, error: 'Forbidden' };
    }
    if (body.status === 'disabled' && tgt.targetIsPrimary) {
      return { ok: false, status: 400, error: 'cannot disable the primary manager; transfer primary first' };
    }
    return { ok: true, status: 200 };
  }

  // ---- provision branch ----
  const role = body.role;
  if (!role || !body.email) {
    return { ok: false, status: 400, error: 'role and email are required' };
  }
  if (role !== 'driver' && role !== 'client' && role !== 'fleet_owner' && role !== 'admin') {
    return { ok: false, status: 400, error: 'Invalid role' };
  }

  // admin provisioning: only admin + system.users.manage
  if (role === 'admin') {
    if (caller.callerRole !== 'admin' || !caller.permissions.has('system.users.manage')) {
      return { ok: false, status: 403, error: 'Forbidden' };
    }
    return { ok: true, status: 200, resolvedPortalRoleCode: 'super_admin' };
  }

  // driver/client: not expanded this iteration — admin only.
  if (role === 'driver' || role === 'client') {
    if (caller.callerRole !== 'admin') {
      return { ok: false, status: 403, error: 'Forbidden' };
    }
    return { ok: true, status: 200 };
  }

  // role === 'fleet_owner' — create a fleet portal user
  const requestedCode = (body.portal_role_code ?? null) as FleetPortalRoleCode | null;
  if (requestedCode && !FLEET_PORTAL_ROLE_CODES.includes(requestedCode)) {
    return { ok: false, status: 400, error: 'portal_role_code must be one of fleet_manager, fleet_dispatcher, fleet_read_only' };
  }

  if (caller.callerRole === 'admin') {
    // HQ may provision any fleet portal role. The first user (no primary yet)
    // must be fleet_manager and becomes primary; later users keep the requested role.
    if (!body.fleet_operator_id) {
      return { ok: false, status: 400, error: 'fleet_operator_id is required to provision a fleet_owner' };
    }
    const code: FleetPortalRoleCode = requestedCode ?? 'fleet_manager';
    if (!fleet?.hasPrimaryManager && code !== 'fleet_manager') {
      return {
        ok: false,
        status: 400,
        error: 'first fleet user must be fleet_manager (primary manager)',
      };
    }
    return {
      ok: true,
      status: 200,
      resolvedFleetOperatorId: body.fleet_operator_id,
      resolvedPortalRoleCode: code,
    };
  }

  // caller is fleet_owner
  if (!caller.permissions.has('team.manage')) {
    return { ok: false, status: 403, error: 'Forbidden' };
  }
  if (!caller.callerFleetId) {
    return { ok: false, status: 403, error: 'Forbidden' };
  }
  if (body.fleet_operator_id && body.fleet_operator_id !== caller.callerFleetId) {
    return { ok: false, status: 403, error: 'cannot provision users for a different fleet' };
  }
  const resolvedFleetOperatorId = caller.callerFleetId;
  const resolvedCode: FleetPortalRoleCode = requestedCode ?? 'fleet_manager';
  if (resolvedCode === 'fleet_manager' && !caller.isPrimaryManager) {
    return { ok: false, status: 403, error: 'only the primary manager can invite a fleet_manager' };
  }
  return {
    ok: true,
    status: 200,
    resolvedFleetOperatorId,
    resolvedPortalRoleCode: resolvedCode,
  };
}

/**
 * Classify an email-conflict scenario for a fleet_owner provision attempt.
 * Returns a user-facing Chinese error message per spec §4 "统一错误与边界".
 */
export function describeEmailConflict(
  existingIdentityRole: string | null | undefined,
  existingFleetId: string | null | undefined,
  intendedFleetId: string,
): string {
  if (existingIdentityRole === 'admin' || (existingFleetId === null && existingIdentityRole !== 'fleet_owner')) {
    return '邮箱已是平台账号，无法加入车队';
  }
  if (existingFleetId && existingFleetId !== intendedFleetId) {
    return '邮箱已在其他车队，无法加入本车队';
  }
  return '邮箱已是本车队成员';
}
